/**
 * The companion: the small figure that sits in the chat console and reacts.
 *
 * His note: "Giving Baby Youda not Youda, A bit of life, can we make it interacting? A live with
 * me? Or simply more integrating in the Design?"
 *
 * WHAT "ALIVE" MEANS HERE, because there is a wrong version of this that would be worse than a
 * still image. A figure that animates constantly is a distraction on a surface where he is reading
 * an answer, and one that reacts to nothing is a GIF. So it moves for exactly two reasons, and
 * both of them are true statements about what the machine is doing:
 *
 *  1. IT LOOKS AT HIM. A small parallax toward the pointer, capped at a few pixels. That is the
 *     entire "alive with me" effect and it costs one listener: the eye reads a figure that tracks
 *     it as present in a way that no amount of idle animation achieves.
 *  2. IT WAITS WITH HIM. While a model call is in flight it leans in and its glow pulses in the
 *     LANE's colour, so the character is a second, peripheral answer to "is this thing working",
 *     next to the light bank. When the answer lands it settles.
 *
 * Otherwise it breathes, very slowly, and does nothing else.
 *
 * THE POINTER TRACKING IS THROTTLED TO A FRAME and never triggers React state, which is why it is
 * a ref writing a CSS variable rather than `useState`. A mousemove handler that re-renders the
 * overlay would re-render the whole thread on every pixel, which on a long conversation is exactly
 * the sort of thing that makes a chat feel heavy for a reason nobody can find.
 */

import { useEffect, useRef } from 'react';

export function Companion({
  lane,
  waiting,
}: {
  lane: 'brain' | 'zou';
  /** A model call is in flight. The figure leans in and pulses while it is. */
  waiting: boolean;
}): JSX.Element {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    /* Honour the setting properly: no tracking at all, not merely a shorter transition. Someone
       who has asked for less motion has not asked for a figure that follows their cursor. */
    if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true) return;

    let frame = 0;
    let pending: { x: number; y: number } | null = null;

    const apply = (): void => {
      frame = 0;
      if (pending === null) return;
      const box = el.getBoundingClientRect();
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      /*
       * Normalised to a generous radius and clamped, so the figure drifts a few pixels rather
       * than snapping around the panel. 260px is roughly "the pointer is near me"; further away
       * and it holds still at the edge of its range, which is what a head turn actually does.
       */
      const dx = Math.max(-1, Math.min(1, (pending.x - cx) / 260));
      const dy = Math.max(-1, Math.min(1, (pending.y - cy) / 260));
      el.style.setProperty('--look-x', `${(dx * 6).toFixed(2)}px`);
      el.style.setProperty('--look-y', `${(dy * 4).toFixed(2)}px`);
    };

    const onMove = (e: PointerEvent): void => {
      pending = { x: e.clientX, y: e.clientY };
      // One update per frame at most. Without this a fast drag runs the maths hundreds of times.
      if (frame === 0) frame = requestAnimationFrame(apply);
    };

    globalThis.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      globalThis.removeEventListener('pointermove', onMove);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <span className="mo-companion" ref={ref} data-lane={lane} data-waiting={waiting} aria-hidden="true">
      <img className="mo-companion-fig" src="/char-companion.webp" alt="" width={216} height={162} />
      {/* The pad it sits on. A figure with no ground floats; a figure on a lit disc is standing
          in the room with him, which is the whole difference between present and pasted on. */}
      <span className="mo-companion-pad" />
    </span>
  );
}
